import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-sm)] font-[var(--font-weight-medium)] transition-all duration-[var(--duration-fast)] ease-[var(--easing-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] active:ease-[var(--easing-spring)]',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-accent)] text-[var(--color-text-inverse)] hover:bg-[var(--color-accent-700)] active:bg-[var(--color-accent-800)] shadow-sm focus-visible:ring-[var(--color-accent)] focus-visible:shadow-[var(--shadow-glow)]',
        secondary:
          'bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)] border border-[var(--color-border-subtle)] hover:bg-[var(--color-surface-tertiary)] active:bg-[var(--color-secondary-200)] focus-visible:ring-[var(--color-focus-ring)]',
        outline:
          'border border-[var(--color-border-default)] bg-[var(--color-surface-primary)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-secondary)] active:bg-[var(--color-surface-tertiary)] focus-visible:ring-[var(--color-focus-ring)]',
        ghost: 'bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-primary)] active:bg-[var(--color-surface-tertiary)] focus-visible:ring-[var(--color-focus-ring)]',
        destructive:
          'bg-[var(--color-error)] text-[var(--color-text-inverse)] hover:bg-[var(--color-error-600)] active:bg-[var(--color-error-600)] shadow-sm focus-visible:ring-[var(--color-error)] focus-visible:shadow-[0_0_20px_rgba(255,59,48,0.3)]',
        link: 'text-[var(--color-accent)] underline-offset-4 hover:underline bg-transparent focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-4',
      },
      size: {
        default: 'h-[var(--button-height-md)] px-[var(--spacing-md)] py-0 text-[var(--font-size-sm)]',
        sm: 'h-[var(--button-height-sm)] px-[var(--spacing-md)] text-[var(--font-size-xs)]',
        lg: 'h-[var(--button-height-lg)] px-[var(--spacing-xl)] text-[var(--font-size-base)]',
        icon: 'h-[var(--button-height-md)] w-[var(--button-height-md)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
